/**
 * useProposalsPaginated - Paginação server-side para a listagem de Propostas Geradas
 *
 * Consome a RPC `get_proposals_paginated` (offset-based), espelhando exatamente
 * o padrão já consolidado em useContractsPaginated.ts. Reaproveita a interface
 * de filtros do Kanban de Propostas (`ProposalFilters`) para que Lista e Kanban
 * compartilhem a mesma fonte de filtros.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 2 — Listagem de Propostas)
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ProposalFilters, KanbanProposal } from '@/hooks/useProposalKanbanColumn';

/** Logger condicional centralizado (padrão do projeto) */
const logger = createLogger('ProposalsPaginated');

/** Campos permitidos para ordenação (espelha a whitelist da RPC) */
export type ProposalSortField =
  | 'created_at'
  | 'updated_at'
  | 'sent_at'
  | 'accepted_at'
  | 'expires_at'
  | 'status'
  | 'proposal_number'
  | 'title'
  | 'total'
  | 'mrr_total'
  | 'deal_title'
  | 'contact_name'
  | 'seller_name';

/** Direção de ordenação */
export type ProposalSortDirection = 'asc' | 'desc';

/**
 * Linha retornada pela RPC. É a mesma estrutura do card do Kanban
 * (KanbanProposal) acrescida do total de registros da consulta.
 */
export interface PaginatedProposal extends KanbanProposal {
  /** Total de registros que atendem aos filtros (COUNT(*) OVER()) */
  total_count: number;
}

/** Parâmetros de paginação e ordenação */
interface PaginationParams {
  page: number;
  pageSize: number;
  sortField?: ProposalSortField;
  sortDirection?: ProposalSortDirection;
}

/** Resultado do hook */
interface PaginatedProposalsResult {
  proposals: PaginatedProposal[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Busca propostas com paginação server-side.
 *
 * @param filters - Filtros ativos da listagem (mesma interface do Kanban)
 * @param pagination - Página, tamanho de página e ordenação
 */
export function useProposalsPaginated(
  filters: ProposalFilters | undefined,
  pagination: PaginationParams,
): PaginatedProposalsResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    // A chave inclui filtros e paginação para manter o cache correto
    queryKey: ['proposals-paginated', currentTenant?.id, filters, pagination],
    queryFn: async () => {
      logger.debug('Buscando propostas paginadas', { filters, pagination });

      const { data, error } = await supabase.rpc('get_proposals_paginated', {
        p_page: pagination.page,
        p_page_size: pagination.pageSize,
        p_tenant_hint: currentTenant?.id || null,
        p_workspace_id: filters?.workspace_id || null,
        p_status: filters?.status || null,
        p_search: filters?.search || null,
        p_seller_id: filters?.seller_id || null,
        p_assigned_to: filters?.assigned_to || null,
        p_proposal_type_id: filters?.proposal_type_id || null,
        p_pipeline_id: filters?.pipeline_id || null,
        p_phase_id: filters?.phase_id || null,
        p_deal_id: filters?.deal_id || null,
        p_created_by: filters?.created_by || null,
        p_date_field: filters?.date_field || null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        p_min_total: filters?.min_total ?? null,
        p_max_total: filters?.max_total ?? null,
        p_min_mrr: filters?.min_mrr ?? null,
        p_max_mrr: filters?.max_mrr ?? null,
        p_pdf_status: filters?.pdf_status || null,
        p_public_enabled: filters?.public_enabled ?? null,
        p_viewed: filters?.viewed ?? null,
        p_tag_ids: filters?.tag_ids || null,
        p_sort_field: pagination.sortField || 'created_at',
        p_sort_direction: pagination.sortDirection || 'desc',
      } as any);

      if (error) {
        logger.error('Erro ao buscar propostas paginadas', error);
        throw error;
      }

      // O total vem repetido em cada linha (pattern COUNT(*) OVER())
      const rows = (data || []) as PaginatedProposal[];
      const totalCount = Number(rows[0]?.total_count ?? 0);

      logger.debug(`Recebidas ${rows.length} propostas (total: ${totalCount})`);

      return {
        proposals: rows,
        totalCount,
        totalPages: Math.ceil(totalCount / pagination.pageSize) || 1,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    proposals: query.data?.proposals || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['proposals-paginated', currentTenant?.id],
      });
    },
  };
}
